import { AutoStack, FlexSpinner } from 'components'
import { useDeleteBlobMutation } from 'components/DocumentGenerator/documentGenerator.generated'
import { DownloadLink } from 'components/DownloadLink'
import { FileUpload } from 'components/FileUpload/FileUpload'
import { FormikMultiUserPicker } from 'components/PersonPicker'
import { FormikTagPicker } from 'components/TagPicker/FormikTagPicker'
import { TextEditor } from 'components/TextEditor'
import { FieldArray } from 'formik'
import {
  FormikCheckbox,
  FormikSpinButton,
  FormikTextField,
  FormikToggle,
} from 'formik-office-ui-fabric-react'
import { useAddressesQuery } from 'forms/ProvingDetailsForm/shared/SapExportFrom.generated'
import { useCurrentFormik } from 'hooks/useCurrentFormikContext'
import _ from 'lodash'
import { Blob, ProvingGround, ProvingGroundUpdateType } from 'model'
import { IconButton, ITag, Label, Stack } from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useUpdateProvingGroundMutation } from 'views/AdminProvingGroundsView/AdminProvingGroundsView.generated'
import { Section } from 'views/ProvingDetailsView/Layout'
import * as Yup from 'yup'
import { FieldStack, FieldStackItem, FormStack } from '../FormStack'

export type ProvingGroundFormValues = ProvingGroundUpdateType

export const provingGroundDefaultValues: Partial<ProvingGround> = {
  inactive: false,
  specialNote: '',
}

export const provingGroundFormSchema = Yup.object().shape({
  title: Yup.string().required('Titel ist ein Pflichtfeld').trim(),
  abbr: Yup.string().required('Abkuerzung ist ein Pflichtfeld').trim(),
  countryCode: Yup.string().required('Land ist ein Pflichtfeld').trim(),
})

export const ProvingGroundForm: React.FC<{
  filesUploading?: boolean
  selectedItemId?: string
}> = ({ filesUploading, selectedItemId }) => {
  const { t } = useTranslation()
  const [deleteBlob] = useDeleteBlobMutation()
  const [updateProvingGround] = useUpdateProvingGroundMutation()
  const formikContext = useCurrentFormik()

  const { data, loading } = useAddressesQuery()

  const addresses = data && data.senderRecipients ? data.senderRecipients : []

  const addressPickerOptions: ITag[] = addresses.map((a) => ({
    key: a._id,
    name: `${a.supplierNumber}: ${a.name}, ${a.street}, ${a.postCode} ${a.place}, ${a.countryCode}`,
  }))

  const initialValues = formikContext ? formikContext.initialValues : undefined
  const existingBlock1Files =
    initialValues && initialValues.block1Files
      ? initialValues && initialValues.block1Files
      : []
  const existingBlock2Files =
    initialValues && initialValues.block2Files
      ? initialValues && initialValues.block2Files
      : []
  const existingBlock3Files =
    initialValues && initialValues.block3Files
      ? initialValues && initialValues.block3Files
      : []
  const existingBlock4Files =
    initialValues && initialValues.block4Files
      ? initialValues && initialValues.block4Files
      : []
  const existingBlock5Files =
    initialValues && initialValues.block5Files
      ? initialValues && initialValues.block5Files
      : []
  const existingBlock6Files =
    initialValues && initialValues.block6Files
      ? initialValues && initialValues.block6Files
      : []
  const existingBlock7Files =
    initialValues && initialValues.block7Files
      ? initialValues && initialValues.block7Files
      : []
  const existingBlock8Files =
    initialValues && initialValues.block8Files
      ? initialValues && initialValues.block8Files
      : []
  const existingBlock9Files =
    initialValues && initialValues.block9Files
      ? initialValues && initialValues.block9Files
      : []
  const existingBlock10Files =
    initialValues && initialValues.block10Files
      ? initialValues && initialValues.block10Files
      : []

  const handleUploadComplete = async (files: Partial<Blob>[]) => {
    const blob1: any = existingBlock1Files ? existingBlock1Files : []
    const blob2: any = existingBlock2Files ? existingBlock2Files : []
    const blob3: any = existingBlock3Files ? existingBlock3Files : []
    const blob4: any = existingBlock4Files ? existingBlock4Files : []
    const blob5: any = existingBlock5Files ? existingBlock5Files : []
    const blob6: any = existingBlock6Files ? existingBlock6Files : []
    const blob7: any = existingBlock7Files ? existingBlock7Files : []
    const blob8: any = existingBlock8Files ? existingBlock8Files : []
    const blob9: any = existingBlock9Files ? existingBlock9Files : []
    const blob10: any = existingBlock10Files ? existingBlock10Files : []

    files.forEach((file) => {
      if (_.includes(file.containerUri, 'block1uploadfiles')) {
        blob1.push(file.uri)
      }
      if (_.includes(file.containerUri, 'block2uploadfiles')) {
        blob2.push(file.uri)
      }
      if (_.includes(file.containerUri, 'block3uploadfiles')) {
        blob3.push(file.uri)
      }
      if (_.includes(file.containerUri, 'block4uploadfiles')) {
        blob4.push(file.uri)
      }
      if (_.includes(file.containerUri, 'block5uploadfiles')) {
        blob5.push(file.uri)
      }
      if (_.includes(file.containerUri, 'block6uploadfiles')) {
        blob6.push(file.uri)
      }
      if (_.includes(file.containerUri, 'block7uploadfiles')) {
        blob7.push(file.uri)
      }
      if (_.includes(file.containerUri, 'block8uploadfiles')) {
        blob8.push(file.uri)
      }
      if (_.includes(file.containerUri, 'block9uploadfiles')) {
        blob9.push(file.uri)
      }
      if (_.includes(file.containerUri, 'block10uploadfiles')) {
        blob10.push(file.uri)
      }
    })

    await updateProvingGround({
      variables: {
        _id: initialValues._id,
        update: {
          title: initialValues.title,
          abbr: initialValues.abbr,
          countryCode: initialValues.countryCode,
          inactive: initialValues.inactive,
          block1Files: blob1,
          block2Files: blob2,
          block3Files: blob3,
          block4Files: blob4,
          block5Files: blob5,
          block6Files: blob6,
          block7Files: blob7,
          block8Files: blob8,
          block9Files: blob9,
          block10Files: blob10,
        },
      },
    })
  }

  const handleRemove = (file: any, i: number, name: any) => {
    const blob1: any = existingBlock1Files ? existingBlock1Files : []
    const blob2: any = existingBlock2Files ? existingBlock2Files : []
    const blob3: any = existingBlock3Files ? existingBlock3Files : []
    const blob4: any = existingBlock4Files ? existingBlock4Files : []
    const blob5: any = existingBlock5Files ? existingBlock5Files : []
    const blob6: any = existingBlock6Files ? existingBlock6Files : []
    const blob7: any = existingBlock7Files ? existingBlock7Files : []
    const blob8: any = existingBlock8Files ? existingBlock8Files : []
    const blob9: any = existingBlock9Files ? existingBlock9Files : []
    const blob10: any = existingBlock10Files ? existingBlock10Files : []

    let variables: any

    switch (name) {
      case 'block1files':
        blob1.splice(i, 1)
        variables = {
          variables: {
            _id: initialValues._id,
            update: {
              block1Files: blob1,
            },
          },
        }
        break
      case 'block2files':
        blob2.splice(i, 1)
        variables = {
          variables: {
            _id: initialValues._id,
            update: {
              block2Files: blob2,
            },
          },
        }
        break
      case 'block3files':
        blob3.splice(i, 1)
        variables = {
          variables: {
            _id: initialValues._id,
            update: {
              block3Files: blob3,
            },
          },
        }
        break
      case 'block4files':
        blob4.splice(i, 1)
        variables = {
          variables: {
            _id: initialValues._id,
            update: {
              block4Files: blob4,
            },
          },
        }
        break
      case 'block5files':
        blob5.splice(i, 1)
        variables = {
          variables: {
            _id: initialValues._id,
            update: {
              block5Files: blob5,
            },
          },
        }
        break
      case 'block6files':
        blob6.splice(i, 1)
        variables = {
          variables: {
            _id: initialValues._id,
            update: {
              block6Files: blob6,
            },
          },
        }
        break
      case 'block7files':
        blob7.splice(i, 1)
        variables = {
          variables: {
            _id: initialValues._id,
            update: {
              block7Files: blob7,
            },
          },
        }
        break
      case 'block8files':
        blob8.splice(i, 1)
        variables = {
          variables: {
            _id: initialValues._id,
            update: {
              block8Files: blob8,
            },
          },
        }
        break
      case 'block9files':
        blob9.splice(i, 1)
        variables = {
          variables: {
            _id: initialValues._id,
            update: {
              block9Files: blob9,
            },
          },
        }
        break
      case 'block10files':
        blob10.splice(i, 1)
        variables = {
          variables: {
            _id: initialValues._id,
            update: {
              block10Files: blob10,
            },
          },
        }
        break
      default:
        break
    }

    updateProvingGround({
      variables,
    })
    deleteBlob({
      variables: {
        uri: file,
      },
    })
  }

  const replacedString = (stringToReplace: any) => {
    stringToReplace = stringToReplace.replace(/%20/g, ' ')
    stringToReplace = stringToReplace.replace(/o%CC%88/g, 'ö')
    stringToReplace = stringToReplace.replace(/O%CC%88/g, 'Ö')
    stringToReplace = stringToReplace.replace(/u%CC%88/g, 'ü')
    stringToReplace = stringToReplace.replace(/U%CC%88/g, 'Ü')
    stringToReplace = stringToReplace.replace(/a%CC%88/g, 'ä')
    stringToReplace = stringToReplace.replace(/A%CC%88/g, 'Ä')
    stringToReplace = stringToReplace.replace(/%C3%9F/g, 'ß')
    stringToReplace = stringToReplace.replace(/%2C/g, ',')
    return stringToReplace
  }

  const renderFixedRatesField = (fieldName: string) => (
    <AutoStack tokens={{ childrenGap: -8 }}>
      <FieldStackItem
        name={`fixedRates.use${
          fieldName[0].toUpperCase() + fieldName.slice(1)
        }`}
        render={(fieldProps) => (
          <FormikToggle
            label={t(`field.locationSettings.fixedRates.${fieldName}`)}
            inlineLabel={true}
            onText={'aktiviert'}
            offText={'deaktiviert'}
            {...fieldProps}
          />
        )}
      />
      <FieldStackItem
        name={`fixedRates.${fieldName}`}
        component={FormikTextField}
        style={{ width: 320 }}
        type="number"
        suffix="€"
      />
    </AutoStack>
  )

  return (
    <FormStack>
      <FlexSpinner
        loading={filesUploading || loading}
        labelPosition="left"
        paddingTop={2}
      >
        <FieldStack>
          <FieldStackItem
            name="abbr"
            label={t('field.abbr.label')}
            required={true}
            component={FormikTextField}
          />
          <FieldStackItem
            name="title"
            label={t('field.title.label')}
            required={true}
            component={FormikTextField}
          />
          <FieldStackItem
            name="countryCode"
            label={t('field.countryCode.label')}
            required={true}
            component={FormikTextField}
          />
          <FieldStackItem
            name="specialNote"
            render={(fieldProps) => (
              <TextEditor
                value={fieldProps.field.value}
                onChange={(value) => {
                  fieldProps.form.setFieldValue('specialNote', value || '')
                }}
              />
            )}
          />
        </FieldStack>
        <FieldStack>
          <Section label={t('field.locationSettings.options.section')}>
            <Section label={t('field.locationSettings.options.label')}>
              <FieldStackItem
                name="drivingLessons"
                label={t('field.locationSettings.options.drivingLessons')}
                component={FormikToggle}
              />
              <FieldStackItem
                name="hideFlight"
                label={t('field.locationSettings.options.hideFlight')}
                component={FormikToggle}
              />
              <FieldStackItem
                name="hideFerryConnection"
                label={t('field.locationSettings.options.hideFerryConnection')}
                component={FormikToggle}
              />
              <FieldStackItem
                name="hideNordschleife"
                label={t('field.locationSettings.options.hideNordschleife')}
                component={FormikToggle}
              />
              <FieldStackItem
                name="hideHotels"
                label={t('field.locationSettings.options.hideHotels')}
                component={FormikToggle}
              />
              <FieldStackItem
                name="racewayHours"
                label={t('field.locationSettings.options.racewayHours')}
                component={FormikToggle}
              />
              <FieldStackItem
                name="showNMF"
                label={t('field.locationSettings.options.showNMF')}
                component={FormikToggle}
              />
              <FieldStackItem
                name="showSpecialUsage"
                label={t('field.locationSettings.options.showSpecialUsage')}
                component={FormikToggle}
              />
              <FieldStackItem
                name="deleteEditor"
                label={t('field.locationSettings.options.deleteEditor')}
                component={FormikToggle}
              />
              <FieldStackItem
                name="skipClearing"
                label={t('field.locationSettings.options.skipClearing')}
                component={FormikToggle}
              />
              <FieldStackItem
                name="useUsageTimespansOnly"
                label={t(
                  'field.locationSettings.options.useUsageTimespansOnly'
                )}
                component={FormikToggle}
              />
            </Section>
            <Section
              label={t('field.locationSettings.mandatoryFields.section')}
            >
              <FieldStackItem
                name="licensePlateMandatory"
                label={t(
                  'field.locationSettings.mandatoryFields.licensePlateMandatory'
                )}
                component={FormikToggle}
              />
              <FieldStackItem
                name="participantsMandatory"
                label={t(
                  'field.locationSettings.mandatoryFields.participantsMandatory'
                )}
                component={FormikToggle}
              />
              <FieldStackItem
                name="vehiclesMandatory"
                label={t(
                  'field.locationSettings.mandatoryFields.vehiclesMandatory'
                )}
                component={FormikToggle}
              />
              <FieldStackItem
                name="accomodationsMandatory"
                label={t(
                  'field.locationSettings.mandatoryFields.accomodationsMandatory'
                )}
                component={FormikToggle}
              />
            </Section>
            <Section label={t('field.locationSettings.transports.section')}>
              <FieldStackItem
                name="vehicleTransportToDays"
                label={t(
                  'field.locationSettings.transports.vehicleTransportToDays'
                )}
                component={FormikSpinButton}
                min={0}
                max={30}
              />
              <FieldStackItem
                name="vehicleTransportFromDays"
                label={t(
                  'field.locationSettings.transports.vehicleTransportFromDays'
                )}
                component={FormikSpinButton}
                min={0}
                max={30}
              />
              <Section label="Hintransport" stack={{ horizontal: true }}>
                <FieldStackItem
                  name="outboundTransportSenderId"
                  render={(fieldProps) => (
                    <AutoStack tokens={{ childrenGap: 8 }}>
                      <Label>{'Adresse Absender'}</Label>
                      <FormikTagPicker
                        tags={addressPickerOptions}
                        {...fieldProps}
                      />
                    </AutoStack>
                  )}
                />
                <FieldStackItem
                  name="outboundTransportRecipientId"
                  render={(fieldProps) => (
                    <AutoStack tokens={{ childrenGap: 8 }}>
                      <Label>{'Adresse Empfänger'}</Label>
                      <FormikTagPicker
                        tags={addressPickerOptions}
                        {...fieldProps}
                      />
                    </AutoStack>
                  )}
                />
              </Section>
              <Section label="Rücktransport" stack={{ horizontal: true }}>
                <FieldStackItem
                  name="returnTransportSenderId"
                  render={(fieldProps) => (
                    <AutoStack tokens={{ childrenGap: 8 }}>
                      <Label>{'Adresse Absender'}</Label>
                      <FormikTagPicker
                        tags={addressPickerOptions}
                        {...fieldProps}
                      />
                    </AutoStack>
                  )}
                />
                <FieldStackItem
                  name="returnTransportRecipientId"
                  render={(fieldProps) => (
                    <AutoStack tokens={{ childrenGap: 8 }}>
                      <Label>{'Adresse Empfänger'}</Label>
                      <FormikTagPicker
                        tags={addressPickerOptions}
                        {...fieldProps}
                      />
                    </AutoStack>
                  )}
                />
              </Section>
            </Section>
            <Section label={t('field.locationSettings.notes.section')}>
              <FieldStackItem
                name="noteHeaderVehicles"
                label={t('field.locationSettings.notes.noteHeaderVehicles')}
                component={FormikTextField}
              />
              <FieldStackItem
                name="noteVehicles"
                multiline={true}
                label={t('field.locationSettings.notes.noteVehicles')}
                component={FormikTextField}
              />
              <FieldStackItem
                label={t('field.locationSettings.notes.annotation')}
                name="annotation"
                multiline={true}
                component={FormikTextField}
              />
              <FieldStackItem
                name="noteHeaderMaterials"
                label={t('field.locationSettings.notes.noteHeaderMaterials')}
                component={FormikTextField}
              />
              <FieldStackItem
                name="noteMaterials"
                multiline={true}
                label={t('field.locationSettings.notes.noteMaterials')}
                component={FormikTextField}
              />
            </Section>
            <Section label={t('field.locationSettings.permissions.section')}>
              <Stack.Item className="composite-field">
                <Label>
                  {t('field.locationSettings.permissions.clearing')}
                </Label>
                <FieldStackItem
                  name={'clearingIds'}
                  component={FormikMultiUserPicker}
                />
              </Stack.Item>
              <Stack.Item className="composite-field">
                <Label>{t('field.locationSettings.permissions.editors')}</Label>
                <FieldStackItem
                  name={'editorsIds'}
                  component={FormikMultiUserPicker}
                />
              </Stack.Item>
              <Stack.Item className="composite-field">
                <Label>
                  {t('field.locationSettings.permissions.resourceManagers')}
                </Label>
                <FieldStackItem
                  name={'resourceManagersIds'}
                  component={FormikMultiUserPicker}
                />
              </Stack.Item>
              <Stack.Item className="composite-field">
                <Label>
                  {t('field.locationSettings.permissions.authorizedEditors')}
                </Label>
                <FieldStackItem
                  name={'authorizedEditorsIds'}
                  component={FormikMultiUserPicker}
                />
              </Stack.Item>
            </Section>
            <Section label={t('enum.keywordType.dataStorageCategory')}>
              <Section label="Block 1">
                <AutoStack tokens={{ childrenGap: 4 }}>
                  <FieldStackItem
                    label="Überschrift"
                    name="block1"
                    required={true}
                    component={FormikTextField}
                  />
                  <FieldStackItem
                    label={t('field.locationSettings.block.note')}
                    name="block1Note"
                    multiline={true}
                    component={FormikTextField}
                  />
                  <FileUpload
                    parentId={`${selectedItemId}block1UploadFiles`}
                    parentType="block1UploadFiles"
                    noDragEventsBubbling={true}
                    onUploadCompleted={handleUploadComplete}
                  />
                  {existingBlock1Files && existingBlock1Files.length > 0 && (
                    <FieldArray name="block1files">
                      {({ form, name, ...helpers }) => {
                        const block1files: string[] =
                          form.values.block1Files || []
                        return (
                          <AutoStack>
                            {block1files.map((file: string, i: number) => (
                              <FieldStack key={i} horizontal={true}>
                                <FieldStackItem
                                  name={`block1files[${i}]`}
                                  key={`block1files[${i}]`}
                                  render={(fieldProps) => (
                                    <DownloadLink
                                      uri={file}
                                      styles={{
                                        root: {
                                          alignSelf: 'start',
                                        },
                                      }}
                                    >
                                      {replacedString(file.split('/').pop())}
                                    </DownloadLink>
                                  )}
                                />
                                <IconButton
                                  styles={{
                                    root: { color: 'red', marginTop: 'auto' },
                                  }}
                                  iconProps={{ iconName: 'Delete' }}
                                  onClick={() => handleRemove(file, i, name)}
                                />
                              </FieldStack>
                            ))}
                          </AutoStack>
                        )
                      }}
                    </FieldArray>
                  )}
                </AutoStack>
              </Section>
              <Section label="Block 2">
                <AutoStack tokens={{ childrenGap: 4 }}>
                  <FieldStackItem
                    label="Überschrift"
                    name="block2"
                    required={true}
                    component={FormikTextField}
                  />
                  <FieldStackItem
                    label={t('field.locationSettings.block.note')}
                    name="block2Note"
                    multiline={true}
                    component={FormikTextField}
                  />
                  <FileUpload
                    parentId={`${selectedItemId}block2UploadFiles`}
                    parentType="block2UploadFiles"
                    noDragEventsBubbling={true}
                    onUploadCompleted={handleUploadComplete}
                  />
                  {existingBlock2Files && existingBlock2Files.length > 0 && (
                    <FieldArray name="block2files">
                      {({ form, name, ...helpers }) => {
                        const block2Files: string[] =
                          form.values.block2Files || []
                        return (
                          <AutoStack>
                            {block2Files.map((file: string, i: number) => (
                              <FieldStack key={i} horizontal={true}>
                                <FieldStackItem
                                  name={`block2files[${i}]`}
                                  key={`block2files[${i}]`}
                                  render={(fieldProps) => (
                                    <DownloadLink
                                      uri={file}
                                      styles={{
                                        root: {
                                          alignSelf: 'start',
                                        },
                                      }}
                                    >
                                      {replacedString(file.split('/').pop())}
                                    </DownloadLink>
                                  )}
                                />
                                <IconButton
                                  styles={{
                                    root: { color: 'red', marginTop: 'auto' },
                                  }}
                                  iconProps={{ iconName: 'Delete' }}
                                  onClick={() => handleRemove(file, i, name)}
                                />
                              </FieldStack>
                            ))}
                          </AutoStack>
                        )
                      }}
                    </FieldArray>
                  )}
                </AutoStack>
              </Section>
              <Section label="Block 3">
                <AutoStack tokens={{ childrenGap: 4 }}>
                  <FieldStackItem
                    label="Überschrift"
                    name="block3"
                    required={true}
                    component={FormikTextField}
                  />
                  <FieldStackItem
                    label={t('field.locationSettings.block.note')}
                    name="block3Note"
                    multiline={true}
                    component={FormikTextField}
                  />
                  <FileUpload
                    parentId={`${selectedItemId}block3UploadFiles`}
                    parentType="block3UploadFiles"
                    noDragEventsBubbling={true}
                    onUploadCompleted={handleUploadComplete}
                  />
                  {existingBlock3Files && existingBlock3Files.length > 0 && (
                    <FieldArray name="block3files">
                      {({ form, name, ...helpers }) => {
                        const block3files: string[] =
                          form.values.block3Files || []
                        return (
                          <AutoStack>
                            {block3files.map((file: string, i: number) => (
                              <FieldStack key={i} horizontal={true}>
                                <FieldStackItem
                                  name={`block3files[${i}]`}
                                  key={`block3files[${i}]`}
                                  render={(fieldProps) => (
                                    <DownloadLink
                                      uri={file}
                                      styles={{
                                        root: {
                                          alignSelf: 'start',
                                        },
                                      }}
                                    >
                                      {replacedString(file.split('/').pop())}
                                    </DownloadLink>
                                  )}
                                />
                                <IconButton
                                  styles={{
                                    root: { color: 'red', marginTop: 'auto' },
                                  }}
                                  iconProps={{ iconName: 'Delete' }}
                                  onClick={() => handleRemove(file, i, name)}
                                />
                              </FieldStack>
                            ))}
                          </AutoStack>
                        )
                      }}
                    </FieldArray>
                  )}
                </AutoStack>
              </Section>
              <Section label="Block 4">
                <AutoStack tokens={{ childrenGap: 4 }}>
                  <FieldStackItem
                    label="Überschrift"
                    name="block4"
                    required={true}
                    component={FormikTextField}
                  />
                  <FieldStackItem
                    label={t('field.locationSettings.block.note')}
                    name="block4Note"
                    multiline={true}
                    component={FormikTextField}
                  />
                  <FileUpload
                    parentId={`${selectedItemId}block4UploadFiles`}
                    parentType="block4UploadFiles"
                    noDragEventsBubbling={true}
                    onUploadCompleted={handleUploadComplete}
                  />
                  {existingBlock4Files && existingBlock4Files.length > 0 && (
                    <FieldArray name="block4files">
                      {({ form, name, ...helpers }) => {
                        const block4files: string[] =
                          form.values.block4Files || []
                        return (
                          <AutoStack>
                            {block4files.map((file: string, i: number) => (
                              <FieldStack key={i} horizontal={true}>
                                <FieldStackItem
                                  name={`block4files[${i}]`}
                                  key={`block4files[${i}]`}
                                  render={(fieldProps) => (
                                    <DownloadLink
                                      uri={file}
                                      styles={{
                                        root: {
                                          alignSelf: 'start',
                                        },
                                      }}
                                    >
                                      {replacedString(file.split('/').pop())}
                                    </DownloadLink>
                                  )}
                                />
                                <IconButton
                                  styles={{
                                    root: { color: 'red', marginTop: 'auto' },
                                  }}
                                  iconProps={{ iconName: 'Delete' }}
                                  onClick={() => handleRemove(file, i, name)}
                                />
                              </FieldStack>
                            ))}
                          </AutoStack>
                        )
                      }}
                    </FieldArray>
                  )}
                </AutoStack>
              </Section>
              <Section label="Block 5">
                <AutoStack tokens={{ childrenGap: 4 }}>
                  <FieldStackItem
                    label="Überschrift"
                    name="block5"
                    required={true}
                    component={FormikTextField}
                  />
                  <FieldStackItem
                    label={t('field.locationSettings.block.note')}
                    name="block5Note"
                    multiline={true}
                    component={FormikTextField}
                  />
                  <FileUpload
                    parentId={`${selectedItemId}block5UploadFiles`}
                    parentType="block5UploadFiles"
                    noDragEventsBubbling={true}
                    onUploadCompleted={handleUploadComplete}
                  />
                  {existingBlock5Files && existingBlock5Files.length > 0 && (
                    <FieldArray name="block5files">
                      {({ form, name, ...helpers }) => {
                        const block5files: string[] =
                          form.values.block5Files || []
                        return (
                          <AutoStack>
                            {block5files.map((file: string, i: number) => (
                              <FieldStack key={i} horizontal={true}>
                                <FieldStackItem
                                  name={`block5files[${i}]`}
                                  key={`block5files[${i}]`}
                                  render={(fieldProps) => (
                                    <DownloadLink
                                      uri={file}
                                      styles={{
                                        root: {
                                          alignSelf: 'start',
                                        },
                                      }}
                                    >
                                      {replacedString(file.split('/').pop())}
                                    </DownloadLink>
                                  )}
                                />
                                <IconButton
                                  styles={{
                                    root: { color: 'red', marginTop: 'auto' },
                                  }}
                                  iconProps={{ iconName: 'Delete' }}
                                  onClick={() => handleRemove(file, i, name)}
                                />
                              </FieldStack>
                            ))}
                          </AutoStack>
                        )
                      }}
                    </FieldArray>
                  )}
                </AutoStack>
              </Section>
              <Section label="Block 6">
                <AutoStack tokens={{ childrenGap: 4 }}>
                  <FieldStackItem
                    label="Überschrift"
                    name="block6"
                    required={true}
                    component={FormikTextField}
                  />
                  <FieldStackItem
                    label={t('field.locationSettings.block.note')}
                    name="block6Note"
                    multiline={true}
                    component={FormikTextField}
                  />
                  <FileUpload
                    parentId={`${selectedItemId}block6UploadFiles`}
                    parentType="block6UploadFiles"
                    noDragEventsBubbling={true}
                    onUploadCompleted={handleUploadComplete}
                  />
                  {existingBlock6Files && existingBlock6Files.length > 0 && (
                    <FieldArray name="block6files">
                      {({ form, name, ...helpers }) => {
                        const block6files: string[] =
                          form.values.block6Files || []
                        return (
                          <AutoStack>
                            {block6files.map((file: string, i: number) => (
                              <FieldStack key={i} horizontal={true}>
                                <FieldStackItem
                                  name={`block6files[${i}]`}
                                  key={`block6files[${i}]`}
                                  render={(fieldProps) => (
                                    <DownloadLink
                                      uri={file}
                                      styles={{
                                        root: {
                                          alignSelf: 'start',
                                        },
                                      }}
                                    >
                                      {replacedString(file.split('/').pop())}
                                    </DownloadLink>
                                  )}
                                />
                                <IconButton
                                  styles={{
                                    root: { color: 'red', marginTop: 'auto' },
                                  }}
                                  iconProps={{ iconName: 'Delete' }}
                                  onClick={() => handleRemove(file, i, name)}
                                />
                              </FieldStack>
                            ))}
                          </AutoStack>
                        )
                      }}
                    </FieldArray>
                  )}
                </AutoStack>
              </Section>
              <Section label="Block 7">
                <AutoStack tokens={{ childrenGap: 4 }}>
                  <FieldStackItem
                    label="Überschrift"
                    name="block7"
                    required={true}
                    component={FormikTextField}
                  />
                  <FieldStackItem
                    label={t('field.locationSettings.block.note')}
                    name="block7Note"
                    multiline={true}
                    component={FormikTextField}
                  />
                  <FileUpload
                    parentId={`${selectedItemId}block7UploadFiles`}
                    parentType="block7UploadFiles"
                    noDragEventsBubbling={true}
                    onUploadCompleted={handleUploadComplete}
                  />
                  {existingBlock7Files && existingBlock7Files.length > 0 && (
                    <FieldArray name="block7files">
                      {({ form, name, ...helpers }) => {
                        const block7files: string[] =
                          form.values.block7Files || []
                        return (
                          <AutoStack>
                            {block7files.map((file: string, i: number) => (
                              <FieldStack key={i} horizontal={true}>
                                <FieldStackItem
                                  name={`block7files[${i}]`}
                                  key={`block7files[${i}]`}
                                  render={(fieldProps) => (
                                    <DownloadLink
                                      uri={file}
                                      styles={{
                                        root: {
                                          alignSelf: 'start',
                                        },
                                      }}
                                    >
                                      {replacedString(file.split('/').pop())}
                                    </DownloadLink>
                                  )}
                                />
                                <IconButton
                                  styles={{
                                    root: { color: 'red', marginTop: 'auto' },
                                  }}
                                  iconProps={{ iconName: 'Delete' }}
                                  onClick={() => handleRemove(file, i, name)}
                                />
                              </FieldStack>
                            ))}
                          </AutoStack>
                        )
                      }}
                    </FieldArray>
                  )}
                </AutoStack>
              </Section>
              <Section label="Block 8">
                <AutoStack tokens={{ childrenGap: 4 }}>
                  <FieldStackItem
                    label="Überschrift"
                    name="block8"
                    required={true}
                    component={FormikTextField}
                  />
                  <FieldStackItem
                    label={t('field.locationSettings.block.note')}
                    name="block8Note"
                    multiline={true}
                    component={FormikTextField}
                  />
                  <FileUpload
                    parentId={`${selectedItemId}block8UploadFiles`}
                    parentType="block8UploadFiles"
                    noDragEventsBubbling={true}
                    onUploadCompleted={handleUploadComplete}
                  />
                  {existingBlock8Files && existingBlock8Files.length > 0 && (
                    <FieldArray name="block8files">
                      {({ form, name, ...helpers }) => {
                        const block8files: string[] =
                          form.values.block8Files || []
                        return (
                          <AutoStack>
                            {block8files.map((file: string, i: number) => (
                              <FieldStack key={i} horizontal={true}>
                                <FieldStackItem
                                  name={`block8files[${i}]`}
                                  key={`block8files[${i}]`}
                                  render={(fieldProps) => (
                                    <DownloadLink
                                      uri={file}
                                      styles={{
                                        root: {
                                          alignSelf: 'start',
                                        },
                                      }}
                                    >
                                      {replacedString(file.split('/').pop())}
                                    </DownloadLink>
                                  )}
                                />
                                <IconButton
                                  styles={{
                                    root: { color: 'red', marginTop: 'auto' },
                                  }}
                                  iconProps={{ iconName: 'Delete' }}
                                  onClick={() => handleRemove(file, i, name)}
                                />
                              </FieldStack>
                            ))}
                          </AutoStack>
                        )
                      }}
                    </FieldArray>
                  )}
                </AutoStack>
              </Section>
              <Section label="Block 9">
                <AutoStack tokens={{ childrenGap: 4 }}>
                  <FieldStackItem
                    label="Überschrift"
                    name="block9"
                    required={true}
                    component={FormikTextField}
                  />
                  <FieldStackItem
                    label={t('field.locationSettings.block.note')}
                    name="block9Note"
                    multiline={true}
                    component={FormikTextField}
                  />
                  <FileUpload
                    parentId={`${selectedItemId}block9UploadFiles`}
                    parentType="block9UploadFiles"
                    noDragEventsBubbling={true}
                    onUploadCompleted={handleUploadComplete}
                  />
                  {existingBlock9Files && existingBlock9Files.length > 0 && (
                    <FieldArray name="block9files">
                      {({ form, name, ...helpers }) => {
                        const block9files: string[] =
                          form.values.block9Files || []
                        return (
                          <AutoStack>
                            {block9files.map((file: string, i: number) => (
                              <FieldStack key={i} horizontal={true}>
                                <FieldStackItem
                                  name={`block9files[${i}]`}
                                  key={`block9files[${i}]`}
                                  render={(fieldProps) => (
                                    <DownloadLink
                                      uri={file}
                                      styles={{
                                        root: {
                                          alignSelf: 'start',
                                        },
                                      }}
                                    >
                                      {replacedString(file.split('/').pop())}
                                    </DownloadLink>
                                  )}
                                />
                                <IconButton
                                  styles={{
                                    root: { color: 'red', marginTop: 'auto' },
                                  }}
                                  iconProps={{ iconName: 'Delete' }}
                                  onClick={() => handleRemove(file, i, name)}
                                />
                              </FieldStack>
                            ))}
                          </AutoStack>
                        )
                      }}
                    </FieldArray>
                  )}
                </AutoStack>
              </Section>
              <Section label="Block 10">
                <AutoStack tokens={{ childrenGap: 4 }}>
                  <FieldStackItem
                    label="Überschrift"
                    name="block10"
                    required={true}
                    component={FormikTextField}
                  />
                  <FieldStackItem
                    label={t('field.locationSettings.block.note')}
                    name="block10Note"
                    multiline={true}
                    component={FormikTextField}
                  />
                  <FileUpload
                    parentId={`${selectedItemId}block10UploadFiles`}
                    parentType="block10UploadFiles"
                    noDragEventsBubbling={true}
                    onUploadCompleted={handleUploadComplete}
                  />
                  {existingBlock10Files && existingBlock10Files.length > 0 && (
                    <FieldArray name="block10files">
                      {({ form, name, ...helpers }) => {
                        const block10files: string[] =
                          form.values.block10Files || []
                        return (
                          <AutoStack>
                            {block10files.map((file: string, i: number) => (
                              <FieldStack key={i} horizontal={true}>
                                <FieldStackItem
                                  name={`block10files[${i}]`}
                                  key={`block10files[${i}]`}
                                  render={(fieldProps) => (
                                    <DownloadLink
                                      uri={file}
                                      styles={{
                                        root: {
                                          alignSelf: 'start',
                                        },
                                      }}
                                    >
                                      {replacedString(file.split('/').pop())}
                                    </DownloadLink>
                                  )}
                                />
                                <IconButton
                                  styles={{
                                    root: { color: 'red', marginTop: 'auto' },
                                  }}
                                  iconProps={{ iconName: 'Delete' }}
                                  onClick={() => handleRemove(file, i, name)}
                                />
                              </FieldStack>
                            ))}
                          </AutoStack>
                        )
                      }}
                    </FieldArray>
                  )}
                </AutoStack>
              </Section>
            </Section>
            <Section label={'Pauschalen zur Kostenschätzung'}>
              <AutoStack horizontal={true} tokens={{ childrenGap: 64 }}>
                <AutoStack tokens={{ childrenGap: 8 }}>
                  {[
                    'participantPerDay',
                    'vehiclePerDay',
                    'feePerDay',
                    'serviceTeamPerDay',
                    'workshopPerDay',
                    'coldcellPerDay',
                    'allowancePerDay',
                    'hotelPerNight',
                  ].map(renderFixedRatesField)}
                </AutoStack>
                <AutoStack tokens={{ childrenGap: 8 }}>
                  {[
                    'compactRentalPerDay',
                    'midSizeRentalPerDay',
                    'fullSizeRentalPerDay',
                    'estateRentalPerDay',
                    'minibusRentalPerDay',
                    'flightPerRoute',
                    'ferryPerParticipant',
                    'ferryPerVehicle',
                  ].map(renderFixedRatesField)}
                </AutoStack>
              </AutoStack>
            </Section>
          </Section>
        </FieldStack>
        <FieldStackItem
          name="inactive"
          label={t('field.inactive.label')}
          component={FormikCheckbox}
        />
      </FlexSpinner>
    </FormStack>
  )
}
